/**
 * Pictogrammes de l'interface.
 *
 * Tracés en SVG plutôt qu'en caractères ou en emoji : eux seuls héritent de
 * `currentColor`, et c'est la couleur qui signale l'état actif. Un emoji
 * resterait obstinément de la même teinte.
 *
 * Le trait est volontairement uniforme (1,8) et les extrémités arrondies,
 * comme la courbe de reste à vivre : une seule main pour tout le dessin.
 */
function Frame({ children, size = 'h-6 w-6' }: { children: React.ReactNode; size?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** Une maison : la convention universelle de l'écran d'accueil. */
export function HomeIcon() {
  return (
    <Frame>
      <path d="M3.2 11 L12 4 L20.8 11" />
      <path d="M5.6 9.8 V20 H18.4 V9.8" />
      <path d="M9.9 20 V14.6 H14.1 V20" />
    </Frame>
  );
}

/** Une liste à puces : des lignes d'écritures. */
export function LedgerIcon() {
  return (
    <Frame>
      <circle cx="4.8" cy="7" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="4.8" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="4.8" cy="17" r="1.1" fill="currentColor" stroke="none" />
      <path d="M8.6 7 H20" />
      <path d="M8.6 12 H20" />
      <path d="M8.6 17 H20" />
    </Frame>
  );
}

/**
 * Une tirelire : le symbole le plus lisible de l'épargne mise de côté.
 *
 * Le groin et l'œil ne sont pas des fioritures — sans eux, le corps ovale ne
 * se lit que comme un galet. Ce sont eux qui font basculer la forme du côté
 * de l'animal.
 */
export function PiggyIcon() {
  return (
    <Frame>
      <ellipse cx="11" cy="13.4" rx="7.4" ry="5.6" />
      <ellipse cx="18.6" cy="13.2" rx="2" ry="1.7" />
      <path d="M12.9 8.9 L14.3 6.5 L15.9 9.1" />
      <path d="M6.4 9.8 H9.6" />
      <path d="M7 18.8 V20.6" />
      <path d="M14.4 18.8 V20.6" />
      <circle cx="15.1" cy="12.4" r="0.8" fill="currentColor" stroke="none" />
    </Frame>
  );
}

/** Deux flèches en boucle : ce qui revient chaque mois. */
export function RecurringIcon() {
  return (
    <Frame>
      <path d="M5 9.4 A7.6 7.6 0 0 1 18.7 8.4" />
      <path d="M15.1 8.1 L19 8.6 L19.4 4.7" />
      <path d="M19 14.6 A7.6 7.6 0 0 1 5.3 15.6" />
      <path d="M8.9 15.9 L5 15.4 L4.6 19.3" />
    </Frame>
  );
}

/** Un crayon : modifier, sans avoir à écrire le mot. */
export function PencilIcon() {
  return (
    <Frame size="h-4 w-4">
      <path d="M4 20 L4.8 16.2 L15.8 5.2 A2.4 2.4 0 0 1 19.2 8.6 L8.2 19.6 Z" />
      <path d="M14.2 6.8 L17.6 10.2" />
    </Frame>
  );
}

/** Deux flèches opposées : un mouvement d'argent entre deux comptes. */
export function TransferIcon() {
  return (
    <Frame size="h-4 w-4">
      <path d="M4 9 H17" />
      <path d="M13.6 5.4 L17.4 9 L13.6 12.6" />
      <path d="M20 15 H7" />
      <path d="M10.4 11.4 L6.6 15 L10.4 18.6" />
    </Frame>
  );
}

/** Un engrenage : la convention des réglages. */
export function SettingsIcon() {
  return (
    <Frame>
      {Array.from({ length: 8 }, (_, index) => {
        const angle = (index * Math.PI) / 4;
        return (
          <line
            key={index}
            x1={(12 + 6.6 * Math.cos(angle)).toFixed(2)}
            y1={(12 + 6.6 * Math.sin(angle)).toFixed(2)}
            x2={(12 + 9.4 * Math.cos(angle)).toFixed(2)}
            y2={(12 + 9.4 * Math.sin(angle)).toFixed(2)}
          />
        );
      })}
      <circle cx="12" cy="12" r="6.4" />
      <circle cx="12" cy="12" r="2.4" />
    </Frame>
  );
}
